import type { DbExecutor } from "./executor";
import { generateId } from "./ids";
import { ValidationError, type ProjectRepository } from "./repositories/projectRepository";
import type { AcquisitionRepository } from "./repositories/acquisitionRepository";
import { EMPTY_FACILITY, type FinancingRepository } from "./repositories/financingRepository";
import type { CalibrationService } from "./calibrationService";
import type { ProjectFinancialsService } from "./projectFinancialsService";
import {
  EMPTY_ASSUMPTIONS,
  validateCapitalSource,
  validateDealInput,
  type CapitalKind,
  type Deal,
  type DealAssumptions,
  type DealCapitalSource,
  type DealInput,
  type DealStatus,
  type DealStrategy,
  type DealUnderwritingSnapshot,
} from "../domain/deal";
import { analyzeDeal, DEAL_ENGINE_VERSION, mulBps } from "../engine/dealAnalysis";
import type { Cents } from "../domain/money";

/**
 * DEALS (spec §87) — the one place deal rows are read and written, and the
 * one path by which a deal becomes a project.
 *
 * FROZEN AFTER CONVERSION: once a deal has become a project every write to
 * it is refused (except archiving), because it is now the record of what
 * was believed at acquisition. Changing your mind is "Duplicate", which
 * starts a new, editable deal.
 */

interface DealRow {
  id: string;
  name: string;
  street_address: string;
  city: string;
  state: string;
  zip: string;
  status: string;
  strategy: string;
  purchase_price_cents: number | null;
  arv_cents: number | null;
  living_area_sqft_x100: number | null;
  notes: string;
  assumptions_json: string;
  converted_project_id: string | null;
  converted_at: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

interface CapitalRow {
  id: string;
  deal_id: string;
  kind: string;
  name: string;
  limit_cents: number | null;
  amount_cents: number;
  rate_pct_x100: number | null;
  amortization_months: number | null;
  interest_only_months: number;
  points_pct_x100: number | null;
  points_on: string;
  fees_cents: number;
  draw_timing: string;
  keep_after_renovation: number;
  sort_order: number;
}

const STATUSES: DealStatus[] = ["evaluating", "offer", "under-contract", "passed", "converted"];
const KINDS: CapitalKind[] = ["bank", "private", "heloc", "business-loc", "seller", "cash", "other"];
const num = (v: number | null | undefined): number | null => (v === null || v === undefined ? null : Number(v));

/** Unknown keys are dropped and missing keys take their empty default — an old row always reads. */
export function parseAssumptions(json: string): DealAssumptions {
  let raw: Record<string, unknown> = {};
  try {
    const parsed = JSON.parse(json) as unknown;
    if (parsed && typeof parsed === "object") raw = parsed as Record<string, unknown>;
  } catch {
    raw = {};
  }
  const out = { ...EMPTY_ASSUMPTIONS } as Record<string, unknown>;
  for (const key of Object.keys(EMPTY_ASSUMPTIONS)) {
    if (key in raw) out[key] = raw[key];
  }
  return out as unknown as DealAssumptions;
}

function mapCapital(row: CapitalRow): DealCapitalSource {
  return {
    id: row.id,
    kind: (KINDS.includes(row.kind as CapitalKind) ? row.kind : "other") as CapitalKind,
    name: row.name,
    limitCents: num(row.limit_cents),
    amountCents: Number(row.amount_cents),
    ratePctX100: num(row.rate_pct_x100),
    amortizationMonths: num(row.amortization_months),
    interestOnlyMonths: Number(row.interest_only_months),
    pointsPctX100: num(row.points_pct_x100),
    pointsOn: row.points_on === "limit" ? "limit" : "amount",
    feesCents: Number(row.fees_cents),
    drawTiming: row.draw_timing === "spread" ? "spread" : "at-closing",
    keepAfterRenovation: Number(row.keep_after_renovation) === 1,
    sortOrder: Number(row.sort_order),
  };
}

function mapDeal(row: DealRow, capital: DealCapitalSource[]): Deal {
  return {
    id: row.id,
    name: row.name,
    streetAddress: row.street_address,
    city: row.city,
    state: row.state,
    zip: row.zip,
    status: (STATUSES.includes(row.status as DealStatus) ? row.status : "evaluating") as DealStatus,
    strategy: (row.strategy === "rental" ? "rental" : "flip") as DealStrategy,
    purchasePriceCents: num(row.purchase_price_cents),
    arvCents: num(row.arv_cents),
    livingAreaSqftX100: num(row.living_area_sqft_x100),
    notes: row.notes,
    assumptions: parseAssumptions(row.assumptions_json),
    capital,
    convertedProjectId: row.converted_project_id,
    convertedAt: row.converted_at,
    archivedAt: row.archived_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export interface RehabBenchmarks {
  profiles: { id: string; name: string; perSqftCents: Cents; allowanceCents: Cents; excludedEachRates: number }[];
  history: {
    samples: { projectName: string; perSqftCents: Cents }[];
    medianPerSqftCents: Cents | null;
    lowPerSqftCents: Cents | null;
    highPerSqftCents: Cents | null;
    /** Fewer than two finished projects is context, never a rate. */
    usable: boolean;
    reason: string;
  };
}

export interface ConversionResult {
  projectId: string;
  created: boolean;
}

export class DealService {
  constructor(
    private readonly db: DbExecutor,
    private readonly projects: ProjectRepository,
    private readonly acquisition: AcquisitionRepository,
    private readonly financing: FinancingRepository,
    private readonly calibration: CalibrationService,
    private readonly projectFinancials: ProjectFinancialsService,
  ) {}

  /* ---------------- reading ---------------- */

  async list(options: { includeArchived?: boolean } = {}): Promise<Deal[]> {
    const rows = await this.db.select<DealRow>(
      `SELECT * FROM deals ${options.includeArchived ? "" : "WHERE archived_at IS NULL"} ORDER BY updated_at DESC`,
    );
    const capital = await this.db.select<CapitalRow>("SELECT * FROM deal_capital_sources ORDER BY sort_order, created_at");
    const byDeal = new Map<string, DealCapitalSource[]>();
    for (const c of capital) {
      const list = byDeal.get(c.deal_id) ?? [];
      list.push(mapCapital(c));
      byDeal.set(c.deal_id, list);
    }
    return rows.map((r) => mapDeal(r, byDeal.get(r.id) ?? []));
  }

  async get(id: string): Promise<Deal | null> {
    const rows = await this.db.select<DealRow>("SELECT * FROM deals WHERE id = ?", [id]);
    if (rows.length === 0) return null;
    const capital = await this.db.select<CapitalRow>(
      "SELECT * FROM deal_capital_sources WHERE deal_id = ? ORDER BY sort_order, created_at",
      [id],
    );
    return mapDeal(rows[0], capital.map(mapCapital));
  }

  /** The deal a project came from, with its frozen underwriting (owner only — never shared). */
  async forProject(projectId: string): Promise<{ deal: Deal; snapshot: DealUnderwritingSnapshot | null } | null> {
    const rows = await this.db.select<{ id: string }>("SELECT id FROM deals WHERE converted_project_id = ?", [projectId]);
    if (rows.length === 0) return null;
    const deal = await this.get(rows[0].id);
    if (!deal) return null;
    return { deal, snapshot: await this.snapshot(deal.id) };
  }

  async snapshot(dealId: string): Promise<DealUnderwritingSnapshot | null> {
    const rows = await this.db.select<{ deal_id: string; project_id: string; frozen_at: string; engine_version: string; snapshot_json: string }>(
      "SELECT * FROM deal_underwriting_snapshots WHERE deal_id = ?",
      [dealId],
    );
    if (rows.length === 0) return null;
    const r = rows[0];
    let deal: DealUnderwritingSnapshot["deal"];
    try {
      deal = JSON.parse(r.snapshot_json) as DealUnderwritingSnapshot["deal"];
      deal.assumptions = parseAssumptions(JSON.stringify(deal.assumptions ?? {}));
    } catch {
      return null;
    }
    return { dealId: r.deal_id, projectId: r.project_id, frozenAt: r.frozen_at, engineVersion: r.engine_version, deal };
  }

  /* ---------------- writing ---------------- */

  private async requireEditable(id: string): Promise<Deal> {
    const deal = await this.get(id);
    if (!deal) throw new ValidationError(["That deal no longer exists."]);
    if (deal.convertedProjectId !== null) {
      throw new ValidationError([
        "This deal became a project, so its underwriting is kept exactly as it was. Duplicate it to explore changes.",
      ]);
    }
    return deal;
  }

  async create(input: DealInput): Promise<Deal> {
    const problems = validateDealInput(input);
    if (problems.length > 0) throw new ValidationError(problems);
    const id = generateId();
    const now = new Date().toISOString();
    const status = input.status === "converted" ? "evaluating" : input.status;
    await this.db.execute(
      `INSERT INTO deals (id, name, street_address, city, state, zip, status, strategy, purchase_price_cents, arv_cents,
         living_area_sqft_x100, notes, assumptions_json, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        id, input.name.trim(), input.streetAddress.trim(), input.city.trim(), input.state.trim(), input.zip.trim(), status,
        input.strategy, input.purchasePriceCents, input.arvCents, input.livingAreaSqftX100, input.notes,
        JSON.stringify(input.assumptions), now, now,
      ],
    );
    return (await this.get(id))!;
  }

  async update(id: string, input: DealInput): Promise<Deal> {
    await this.requireEditable(id);
    const problems = validateDealInput(input);
    if (problems.length > 0) throw new ValidationError(problems);
    if (input.status === "converted") throw new ValidationError(["A deal becomes a project only through Convert to project."]);
    await this.db.execute(
      `UPDATE deals SET name = ?, street_address = ?, city = ?, state = ?, zip = ?, status = ?, strategy = ?,
         purchase_price_cents = ?, arv_cents = ?, living_area_sqft_x100 = ?, notes = ?, assumptions_json = ?, updated_at = ?
       WHERE id = ?`,
      [
        input.name.trim(), input.streetAddress.trim(), input.city.trim(), input.state.trim(), input.zip.trim(), input.status,
        input.strategy, input.purchasePriceCents, input.arvCents, input.livingAreaSqftX100, input.notes,
        JSON.stringify(input.assumptions), new Date().toISOString(), id,
      ],
    );
    return (await this.get(id))!;
  }

  async saveCapital(dealId: string, source: Omit<DealCapitalSource, "sortOrder" | "id"> & { id: string | null }): Promise<Deal> {
    await this.requireEditable(dealId);
    const problems = validateCapitalSource(source);
    if (problems.length > 0) throw new ValidationError(problems);
    const now = new Date().toISOString();
    const values = [
      source.kind, source.name.trim(), source.limitCents, source.amountCents, source.ratePctX100, source.amortizationMonths,
      source.interestOnlyMonths, source.pointsPctX100, source.pointsOn, source.feesCents, source.drawTiming,
      source.keepAfterRenovation ? 1 : 0,
    ];
    if (source.id) {
      const found = await this.db.select<{ id: string }>("SELECT id FROM deal_capital_sources WHERE id = ? AND deal_id = ?", [
        source.id,
        dealId,
      ]);
      if (found.length === 0) throw new ValidationError(["That capital source no longer exists."]);
      await this.db.execute(
        `UPDATE deal_capital_sources SET kind = ?, name = ?, limit_cents = ?, amount_cents = ?, rate_pct_x100 = ?,
           amortization_months = ?, interest_only_months = ?, points_pct_x100 = ?, points_on = ?, fees_cents = ?,
           draw_timing = ?, keep_after_renovation = ?, updated_at = ? WHERE id = ? AND deal_id = ?`,
        [...values, now, source.id, dealId],
      );
    } else {
      const order = await this.db.select<{ n: number | null }>(
        "SELECT MAX(sort_order) AS n FROM deal_capital_sources WHERE deal_id = ?",
        [dealId],
      );
      await this.db.execute(
        `INSERT INTO deal_capital_sources (kind, name, limit_cents, amount_cents, rate_pct_x100, amortization_months,
           interest_only_months, points_pct_x100, points_on, fees_cents, draw_timing, keep_after_renovation,
           id, deal_id, sort_order, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [...values, generateId(), dealId, (Number(order[0]?.n ?? 0) || 0) + 10, now, now],
      );
    }
    await this.touch(dealId);
    return (await this.get(dealId))!;
  }

  async removeCapital(dealId: string, sourceId: string): Promise<Deal> {
    await this.requireEditable(dealId);
    await this.db.execute("DELETE FROM deal_capital_sources WHERE id = ? AND deal_id = ?", [sourceId, dealId]);
    await this.touch(dealId);
    return (await this.get(dealId))!;
  }

  async setStatus(id: string, status: DealStatus): Promise<Deal> {
    await this.requireEditable(id);
    if (status === "converted") throw new ValidationError(["A deal becomes a project only through Convert to project."]);
    await this.db.execute("UPDATE deals SET status = ?, updated_at = ? WHERE id = ?", [status, new Date().toISOString(), id]);
    return (await this.get(id))!;
  }

  /** Archiving is allowed for every deal, converted or not: it hides, it never rewrites. */
  async archive(id: string, archived = true): Promise<void> {
    await this.db.execute("UPDATE deals SET archived_at = ?, updated_at = ? WHERE id = ?", [
      archived ? new Date().toISOString() : null,
      new Date().toISOString(),
      id,
    ]);
  }

  /** A deal that never became a project can be deleted; a converted one is history and cannot. */
  async delete(id: string): Promise<void> {
    await this.requireEditable(id);
    await this.db.transaction(async () => {
      await this.db.execute("DELETE FROM deal_capital_sources WHERE deal_id = ?", [id]);
      await this.db.execute("DELETE FROM deals WHERE id = ?", [id]);
    });
  }

  async duplicate(id: string): Promise<Deal> {
    const source = await this.get(id);
    if (!source) throw new ValidationError(["That deal no longer exists."]);
    return this.db.transaction(async () => {
      const copy = await this.create({
        name: source.name ? `${source.name} (copy)` : "",
        streetAddress: source.streetAddress,
        city: source.city,
        state: source.state,
        zip: source.zip,
        status: "evaluating",
        strategy: source.strategy,
        purchasePriceCents: source.purchasePriceCents,
        arvCents: source.arvCents,
        livingAreaSqftX100: source.livingAreaSqftX100,
        notes: source.notes,
        assumptions: source.assumptions,
      });
      for (const c of source.capital) {
        await this.saveCapital(copy.id, { ...c, id: null });
      }
      return (await this.get(copy.id))!;
    });
  }

  private async touch(id: string): Promise<void> {
    await this.db.execute("UPDATE deals SET updated_at = ? WHERE id = ?", [new Date().toISOString(), id]);
  }

  /* ---------------- rehab benchmarks (§87.8) ---------------- */

  async rehabBenchmarks(): Promise<RehabBenchmarks> {
    const profiles: RehabBenchmarks["profiles"] = [];
    for (const profile of await this.calibration.listProfiles()) {
      const rates = await this.calibration.listRates(profile.id);
      profiles.push({
        id: profile.id,
        name: profile.name,
        perSqftCents: rates.filter((r) => r.driver === "house-sqft").reduce((sum, r) => sum + r.rateCents, 0),
        allowanceCents: rates.filter((r) => r.driver === "allowance").reduce((sum, r) => sum + r.rateCents, 0),
        excludedEachRates: rates.filter((r) => r.driver === "each").length,
      });
    }
    const candidates = await this.db.select<{ id: string; name: string; living_area_sqft_x100: number }>(
      `SELECT p.id, p.name, p.living_area_sqft_x100 FROM projects p
         JOIN project_sales s ON s.project_id = p.id
        WHERE s.closed_at IS NOT NULL AND p.living_area_sqft_x100 > 0`,
    );
    const samples: RehabBenchmarks["history"]["samples"] = [];
    for (const c of candidates) {
      const dashboard = await this.projectFinancials.dashboard(c.id);
      const renovation = dashboard.bucketTotals.renovation ?? 0;
      if (renovation <= 0) continue;
      samples.push({ projectName: c.name, perSqftCents: Math.round((renovation * 100) / Number(c.living_area_sqft_x100)) });
    }
    const sorted = samples.map((s) => s.perSqftCents).sort((x, y) => x - y);
    const median =
      sorted.length === 0
        ? null
        : sorted.length % 2 === 1
          ? sorted[(sorted.length - 1) / 2]
          : Math.round((sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2);
    return {
      profiles,
      history: {
        samples,
        medianPerSqftCents: median,
        lowPerSqftCents: sorted[0] ?? null,
        highPerSqftCents: sorted[sorted.length - 1] ?? null,
        usable: sorted.length >= 2,
        reason:
          sorted.length >= 2
            ? `Median actual renovation cost of ${sorted.length} closed projects with a recorded living area.`
            : sorted.length === 1
              ? "Only one closed project with a living area and recorded renovation costs — shown for context, not used as a rate."
              : "No closed projects with a living area and recorded renovation costs yet.",
      },
    };
  }

  /* ---------------- conversion (§87.12) ---------------- */

  /**
   * One transaction: create the project through the ordinary repository,
   * carry forward what has a home there, freeze the underwriting, link both
   * ways. Idempotent: a converted deal answers with its project, and the
   * partial unique indexes refuse a second project for the same deal even
   * when two requests race.
   */
  async convertToProject(dealId: string): Promise<ConversionResult> {
    // One connection cannot tell a double-click from a nested call, so two
    // conversions started together would share ONE transaction and collide
    // on the job number (found by test). The second request waits for the
    // first and answers with its project.
    const inflight = this.converting.get(dealId);
    if (inflight) return inflight.then((r) => ({ projectId: r.projectId, created: false }));
    const run = this.convertOnce(dealId).finally(() => this.converting.delete(dealId));
    this.converting.set(dealId, run);
    return run;
  }

  private readonly converting = new Map<string, Promise<ConversionResult>>();

  private async convertOnce(dealId: string): Promise<ConversionResult> {
    const existing = await this.get(dealId);
    if (!existing) throw new ValidationError(["That deal no longer exists."]);
    if (existing.convertedProjectId) return { projectId: existing.convertedProjectId, created: false };
    try {
      return await this.db.transaction(async () => {
        const deal = (await this.get(dealId))!;
        if (deal.convertedProjectId) return { projectId: deal.convertedProjectId, created: false };
        const a = deal.assumptions;
        const expectedValue = deal.strategy === "rental" ? a.stabilizedValueCents ?? deal.arvCents : a.salePriceCents ?? deal.arvCents;
        const project = await this.projects.createProject({
          name: deal.name.trim() || deal.streetAddress.trim(),
          projectType: "renovation",
          statusId: "planning",
          streetAddress: deal.streetAddress,
          city: deal.city,
          state: deal.state,
          zip: deal.zip,
          purchasePriceCents: deal.purchasePriceCents,
          expectedArvCents: expectedValue,
          notes: deal.notes,
          strategy: deal.strategy,
          livingAreaSqftX100: deal.livingAreaSqftX100,
        });
        await this.db.execute("UPDATE projects SET source_deal_id = ? WHERE id = ?", [deal.id, project.id]);

        if (a.buyerClosingCostsCents !== null && a.buyerClosingCostsCents > 0) {
          await this.acquisition.create(project.id, {
            kind: "closing-costs",
            description: "Buyer closing costs (from deal analysis)",
            amountCents: a.buyerClosingCostsCents,
            creditedTowardPurchase: false,
            date: null,
            notes: "Estimated when the deal was analyzed — replace with the settlement statement figure.",
          });
        }

        for (const source of deal.capital) {
          if (source.kind === "cash") continue;
          if (source.amountCents <= 0 && (source.limitCents ?? 0) <= 0) continue;
          const pointsCents =
            source.pointsPctX100 === null
              ? 0
              : mulBps(source.pointsOn === "limit" ? source.limitCents ?? source.amountCents : source.amountCents, source.pointsPctX100);
          const loanType = source.kind === "private" ? "private" : source.kind === "business-loc" ? "business" : source.kind === "bank" ? "acquisition-rehab" : "other";
          const terms = [
            source.amortizationMonths === null
              ? "Interest only."
              : `${source.interestOnlyMonths > 0 ? `${source.interestOnlyMonths} months interest only, then ` : ""}amortizing over ${source.amortizationMonths} months.`,
            source.limitCents !== null ? `Facility limit ${(source.limitCents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" })}; ${(source.amountCents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" })} modelled as borrowed.` : "",
            source.drawTiming === "spread" ? "Modelled as drawn over the renovation." : "",
          ]
            .filter(Boolean)
            .join(" ");
          await this.financing.createFacility(project.id, {
            ...EMPTY_FACILITY,
            name: source.name || "Loan",
            loanType,
            commitmentCents: source.limitCents ?? source.amountCents,
            acquisitionFundingCents: source.drawTiming === "at-closing" ? source.amountCents : null,
            rehabFacilityCents: source.drawTiming === "spread" ? source.amountCents : null,
            interestRatePctX100: source.ratePctX100,
            interestBasis: "drawn",
            interestOnly: source.amortizationMonths === null || source.interestOnlyMonths > 0,
            originationFeeCents: pointsCents + source.feesCents > 0 ? pointsCents + source.feesCents : null,
            paymentCadence: "monthly",
            notes: `Carried from the deal analysis. ${terms} Confirm against the loan documents.`,
            termsSource: "manual",
          });
        }

        if (deal.strategy === "rental" || a.monthlyRentCents !== null) {
          await this.financing.saveRental(project.id, {
            monthlyRentCents: a.monthlyRentCents,
            vacancyPctX100: a.vacancyPctX100,
            propertyTaxAnnualCents: a.rentalTaxesAnnualCents,
            insuranceAnnualCents: a.rentalInsuranceAnnualCents,
            hoaMonthlyCents: a.rentalHoaMonthlyCents,
            managementPctX100: a.managementPctX100,
            maintenancePctX100: a.maintenancePctX100,
            utilitiesMonthlyCents: null,
            otherMonthlyCents: a.otherOperatingMonthlyCents,
            leaseUpDays: null,
          });
        }

        const frozenAt = new Date().toISOString();
        const analysis = analyzeDeal(deal);
        await this.db.execute(
          `INSERT INTO deal_underwriting_snapshots (deal_id, project_id, frozen_at, engine_version, snapshot_json)
           VALUES (?, ?, ?, ?, ?)`,
          [deal.id, project.id, frozenAt, DEAL_ENGINE_VERSION, JSON.stringify({ ...deal, status: "converted", analysis })],
        );
        await this.db.execute(
          `UPDATE deals SET converted_project_id = ?, converted_at = ?, status = 'converted', updated_at = ?
            WHERE id = ? AND converted_project_id IS NULL`,
          [project.id, frozenAt, frozenAt, deal.id],
        );
        const linked = await this.db.select<{ converted_project_id: string | null }>(
          "SELECT converted_project_id FROM deals WHERE id = ?",
          [deal.id],
        );
        if (linked[0]?.converted_project_id !== project.id) throw new ConversionRace();
        return { projectId: project.id, created: true };
      });
    } catch (err) {
      // A racing request converted it first (or the unique index refused a
      // second project): answer with the project that exists.
      const after = await this.get(dealId);
      if (after?.convertedProjectId) return { projectId: after.convertedProjectId, created: false };
      throw err;
    }
  }
}

class ConversionRace extends Error {}
